import { EntityNotFound } from '@common/EntityNotFound';
import { CodeGenerationContextProvider } from '@common/git/CodeGeneration/context';
import { GitInfo } from '@common/git/GitInfo';
import { ProjectContextProvider } from '@common/git/Project/context';
import { useProjectState } from '@common/git/Project/hooks';
import { PageHeader } from '@common/Header/PageHeader';
import { JobDetailType } from '@common/queries/job';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { titleCase } from '@common/utils/metadataUtils';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { ExtractArrayType, ExtractUrlPathVariables, Mutable } from '@prophecy/interfaces/generic';
import { EntityDetailQuery } from '@prophecy/queries/common/queries/common';
import { theme } from '@prophecy/theme';
import { Button, Centered, Container, Spinner, Stack, StackItem, TabPane, Tabs } from '@prophecy/ui-v3';
import { DefaultRecordType } from '@prophecy/ui-v3/Table/types';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMixPanel } from '../../../context/mixpanel/context';
import { useGraphQlQuery } from '../../../data/util';
import { CloneJob } from '../../Job/CloneJob';
import { useEntityTableSchema, getEntityTableSortConfigVariables, JobSubEntity } from '../components/Config';
import { InfoEntity } from '../components/EntityInfo';
import { EntityTable } from '../components/EntityTable';
import { EntityTableWithCreateButton } from '../components/EntityTableWithCreateButton';
import { JobInfoFooterSchema, JobInfoSchema, JobPageLayout } from '../config/job';
import { Footer } from '../config/styled';
import { SchemaType } from '../config/types';
import { getMetadataDefaultCrumbs } from './MetadataDetailHeader';
import { CodeTab } from './ProjectDetail/CodeTab';

const StyledStackItem = styled(StackItem)`
  width: 100%;
`;

const StyledInfoEntity = styled(InfoEntity)`
  height: auto;
` as typeof InfoEntity;
type TabType = ExtractArrayType<Mutable<(typeof JobPageLayout)['tabs']>>;
export default function Page() {
  const entity = Entity.Job;
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.Job.tab.url>>();
  const uid = decodeURIComponent(params.uid as string);
  const tab = params.tab as string;
  const variables = { uid };
  const { data, isLoading } = useGraphQlQuery<JobDetailType>(
    [entity, entity + 'Detail', variables],
    EntityDetailQuery(entity),
    undefined,
    variables
  );
  const item = data?.Job;

  if (isLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  if (!item) return <EntityNotFound uid={uid} entity={Entity.Job} projectContext={true} />;
  return (
    <ProjectContextProvider projectId={item?.project?._id as string}>
      <JobDetail uid={uid} job={item} tab={tab} />
    </ProjectContextProvider>
  );
}
function JobDetail({ uid, job, tab }: { uid: string; job: JobDetailType['Job']; tab: string }) {
  const entity = Entity.Job;
  const [activeTab, setActiveTab] = useState(titleCase(tab) || JobPageLayout.firstTab);
  const [isClone, toggleClone] = useState(false);

  const { projectId, currentBranch: branch, isMainBranch, projectName, language } = useProjectState();

  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents.common.metadata.entityOpen, {
      uid,
      name: job.name,
      entity,
      url: window.location.href
    });
  }, [track, uid, job.name, entity]);

  const changeTab = (tab: string) => {
    setActiveTab(tab);
    track(EntityEvents.common.metadata.tabChange, {
      uid,
      name: job.name,
      tab,
      entity,
      url: window.location.href
    });
  };

  const tableSchema = useEntityTableSchema(entity, entity) as SchemaType<DefaultRecordType>;

  const readOnly = isMainBranch;

  const getTabDetail = ({ title, entity }: TabType) => {
    if (title === 'Info') {
      return (
        <Stack gap={theme.spaces.x16} height='100%' align='space-between'>
          {isClone && (
            <CloneJob
              jobUID={uid}
              branch={branch as string}
              targetProjectId={projectId as string}
              targetProjectLanguage={language as BackEndCodeLanguage}
              onClose={() => toggleClone(false)}
            />
          )}
          <Stack direction='horizontal' align='space-between'>
            <Stack gap={theme.spaces.x16} grow='1'>
              <StyledInfoEntity
                readOnly={readOnly}
                hideFooter={true}
                schema={JobInfoSchema()}
                variables={{ uid }}
                projectId={projectId}
              />
            </Stack>
            <Button
              tooltip={readOnly ? 'Cloning is not allowed on main branch as it is read-only' : ''}
              onClick={() => toggleClone(true)}
              variant='secondaryGray'
              disabled={readOnly}
              data-test-id='job-detail-duplicate-button'>
              Duplicate
            </Button>
          </Stack>
          {projectId && (
            <EntityTableWithCreateButton
              uid={uid}
              entity={entity}
              subEntity={JobSubEntity}
              childEntity={Entity.Pipeline}
              projectId={projectId}
            />
          )}
          <StyledInfoEntity
            readOnly={readOnly}
            schema={JobInfoFooterSchema(uid, { _id: projectId, language })}
            variables={{ uid }}
            projectId={projectId}
          />
        </Stack>
      );
    } else if (title === 'Code') {
      return projectId && <CodeTab projectId={projectId} uri={`/${job.path}`}></CodeTab>;
    } else {
      return (
        <EntityTable
          schema={tableSchema}
          variables={{ uid }}
          sortConfigVariables={getEntityTableSortConfigVariables(entity)}
        />
      );
    }
  };
  const getTabUrl = (title: string) => Private_Routes.Job.tab.getUrl({ uid, tab: title.toLowerCase() });

  return (
    <CodeGenerationContextProvider>
      <Stack width='100%' height='100%' align='space-between' alignY='center'>
        <StyledStackItem grow='1'>
          <Stack width='100%' height='100%' align='center' alignY='center'>
            <DocumentTitle title={`${entity}:${job.name || ''}`} />
            <PageHeader crumbs={getMetadataDefaultCrumbs(Entity.Job, projectName, projectId, job)} />
            <Container>
              <Tabs activeKey={activeTab} onChange={(tab) => changeTab(tab)}>
                {JobPageLayout.tabs.map((v) => {
                  const title = v.title;
                  return (
                    <TabPane key={title} tab={title} elementType='link' to={getTabUrl(title)}>
                      {getTabDetail(v)}
                    </TabPane>
                  );
                })}
              </Tabs>
            </Container>
          </Stack>
        </StyledStackItem>
        <Footer>{projectId ? <GitInfo projectId={projectId} /> : null}</Footer>
      </Stack>
    </CodeGenerationContextProvider>
  );
}
